// Other Actions → "Message the customer" (eBay orders only since 2026-10-30;
// docs/contact-view.md). The server builds the draft (GET
// /api/ai/conversations/:id/message-customer, services/other-actions-ebay-message.js)
// and sends it (POST .../message-customer/send): a new eBay message to the
// order's buyer, recorded on this contact (the thread and the Activity Log).
// It never closes the contact or touches its suggestion. Pure helpers, unit
// tested in ui/test/salesHoldPath.test.js and ui/test/messageCustomer.test.js.

import { templateHtmlToPlainText, findUnresolvedPlaceholders } from '@cxhub/shared'

const EBAY_CLIENT_CODES = ['1AE', 'AME']

// Whether the Other Actions button shows: the contact has an order and the
// order is an eBay order (its TRQ client code, 1AE / AME). Hidden otherwise
// (a Website or Walmart order, no order): the reply box reaches those
// customers. The server checks the order's client again from TRQ's master
// index before it builds or sends anything.
export function messageCustomerAvailable(conversation) {
  if (!conversation?.erpOrderId) return false
  return EBAY_CLIENT_CODES.includes(String(conversation.trqClientCode ?? '').toUpperCase())
}

// Why Send Message is disabled, or null. The buyer comes from the order,
// else from the contact's own eBay conversation (2026-11-02), so a missing
// buyer is the last fallback. An INSERT placeholder left in the box stops
// Send, as on every customer-facing send (findUnresolvedPlaceholders()).
export const MISSING_BUYER_REASON = 'Missing buyer ID: neither the order nor an eBay message on this contact names the buyer, so the message cannot be sent.'
export function messageCustomerSendBlock({ message, buyerId, status = null } = {}) {
  if (status === 'processing') return 'Sending…'
  if (!buyerId) return MISSING_BUYER_REASON
  if (!String(message ?? '').trim()) return 'Type a message first.'
  const placeholders = findUnresolvedPlaceholders(message)
  if (placeholders.length) return `Fill in ${placeholders.join(', ')} before sending.`
  return null
}

// The success line: "Sent to <buyer ID> on eBay at <time>", or after
// "Sent response through eBay Portal", "Recorded as sent to <buyer ID> in
// Seller Hub at <time>".
export function messageCustomerSentLine({ buyerId, sentAt, sentInSellerHub = false }, { timeZone } = {}) {
  let time = ''
  try {
    time = new Date(sentAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...(timeZone ? { timeZone } : {}) })
  } catch { time = '' }
  if (sentInSellerHub) return `Recorded as sent to ${buyerId ?? 'the buyer'} in Seller Hub${time ? ` at ${time}` : ''}`
  return `Sent to ${buyerId ?? 'the buyer'} on eBay${time ? ` at ${time}` : ''}`
}

// Where the buyer ID came from, for the line under the window's title.
export function messageCustomerBuyerSource(source) {
  if (source === 'order') return 'Buyer from the order'
  if (source === 'ebay_message') return "Buyer from this contact's eBay message (the order names none)"
  return null
}

// A picked template as the eBay message: the template's plain text, the
// address on file in INSERTADDRESS, and the signature the draft carries.
export function messageFromTemplate(content, { addressBlock = null, signature = null } = {}) {
  let text = templateHtmlToPlainText(String(content ?? '')).trim()
  if (addressBlock && text.includes('INSERTADDRESS')) text = text.replace('INSERTADDRESS', addressBlock)
  return signature ? `${text}\n\n${signature}` : text
}

// The picker's options: the client's eBay folders, grouped by folder, in
// folder then title order.
export function templatePickerOptions(options = []) {
  return [...(options ?? [])]
    .filter((o) => o?.id && o?.title)
    .sort((a, b) => String(a.folder ?? '').localeCompare(String(b.folder ?? '')) || String(a.title).localeCompare(String(b.title)))
}

// The Activity Log line for an Other Actions eBay message.
export function otherActionsMessageActivityLine(entry) {
  const who = entry?.sentBy ?? 'someone'
  if (entry?.outcome === 'sent' && entry?.via === 'seller_hub') return `Sent response through eBay Portal: message to the buyer sent in Seller Hub (eBay daily message limit), recorded by ${who}`
  if (entry?.outcome === 'sent') return `Message sent to the buyer on eBay by ${who}`
  if (entry?.outcome === 'template_filled') return `Template "${entry.templateTitle ?? 'unknown'}" filled for a message to the buyer by ${who}${entry.filled?.length ? ` (filled: ${entry.filled.join(', ')})` : ''}`
  return `Message to the buyer on eBay not sent (${who})`
}
